import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Markdown from "react-markdown";
import { useTranslation } from "react-i18next";
import {
  IconButton,
  OutlinedInput,
  InputAdornment,
  Link,
  Chip,
} from "@mui/material";

import { Select } from "antd";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Message from "./Message";
import Feedback from "./Feedback";
import { Button } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import linkify from "./Utils";

import { getRetrievals } from "../api/RetrievalsApi";
import "./ShowQuestionApiCognitive.css";
import { fetch_stream } from "../utils/streaming";

import Lottie from "lottie-react";
import animationData from "../assets/cogsol-loading.json";

// Import i18n configuration
import "../i18n/i18n";

// Initial values
const MAX_MSG_LENGTH = 1000;
const MAX_RESPONSES = 10;
const MAX_RESP_STATUS = 300;

const ThinkingAnimation = ({ baseText }) => {
  const [dots, setDots] = useState("");
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prevDots) => {
        if (prevDots.length >= 3) return "";
        return prevDots + ".";
      });
    }, 400);

    return () => clearInterval(interval);
  }, []);

  const containerStyle = {
    display: "inline-block",
    minWidth: "100px",
  };

  const dotsContainerStyle = {
    display: "inline-block",
    width: "21px",
    textAlign: "left",
  };

  return (
    <div style={containerStyle}>
      {baseText}
      <span style={dotsContainerStyle}>
        {dots}
        <span style={{ visibility: "hidden" }}>
          {".".repeat(3 - dots.length)}
        </span>
      </span>
    </div>
  );
};

const ShowQuestion = (props) => {
  const { t, i18n } = useTranslation();
  const envRetrieval = Boolean(process.env.REACT_APP_RETRIEVAL);

  const [question, setQuestion] = useState(props.chat ? props.chat : null);
  const [messages, setMessages] = useState(
    props.chat
      ? props.chat.messages.map((msg) => ({ ...msg, attachment: null }))
      : []
  );
  const [ended, setEnded] = useState(props.chat ? props.chat.execution_status === "ENDED" : false);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [iniMessage, setIniMessage] = useState(null);
  const [maxMsgLength, setMaxMsgLength] = useState(MAX_MSG_LENGTH);
  const [maxResponses, setIMaxResponses] = useState(MAX_RESPONSES);
  const [cantResponses, setcantResponses] = useState(1);
  const [endedStatus, setEndedStatus] = useState(0);
  const [activeMatrix, setActiveMatrix] = useState(false);
  const [activatingMatrix, setActivatingMatrix] = useState(false);
  const [matrixModal, setMatrixModal] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [allowAsk, setAllowAsk] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const [retrievals, setRetrievals] = useState([]);
  const [selectedRetrieval, setSelectedRetrieval] = useState(null);
  const [expandedReferences, setExpandedReferences] = useState({});
  const [loading, setLoading] = useState(!props.retrievalId);
  const [streamingAvailable, setStreamingAvailable] = useState(false);
  let [streamEnded, setStreamEnded] = useState(false);
  const [matrixModeAvailable, setMatrixModeAvailable] = useState(false);
  const [audioAvailable, setAudioAvailable] = useState(false);

  const [messageFile, setMessageFile] = useState(null);
  const [attachments, setAttachments] = useState([]);

  const [chatName, setChatName] = useState("");
  const [urlLogo, setUrlLogo] = useState("");
  const [assistantColors, setAssistantColors] = useState(null);

  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const [currentRetrievalDescription, setCurrentRetrievalDescription] =
    useState("");

  const [chatId, setChatId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const [lastFetchedRetrievalId, setLastFetchedRetrievalId] = useState(null);

  // Reset all assistant-specific states
  const resetAssistantStates = useCallback(() => {
    setActiveMatrix(false);
    setActivatingMatrix(false);
    setEndedStatus(0);
    setEnded(false);
    setMessages([]);
    setQuestion(null);
    setcantResponses(1);
    setMessageFile(null);
    setAttachments([]);
    setExpandedReferences({});
    setInputValue("");
    setIsLoading(false);
    setChatId(null);
    setChatName("");
    setUrlLogo("");
    setAssistantColors(null);
  }, []);

  useEffect(() => {
    const handleResetAssistantProperties = () => {
      setChatName("");
      setUrlLogo("");
      setAssistantColors(null);
      resetAssistantStates();
    };
    document.addEventListener(
      "resetAssistantProperties",
      handleResetAssistantProperties
    );
    return () => {
      document.removeEventListener(
        "resetAssistantProperties",
        handleResetAssistantProperties
      );
    };
  }, [resetAssistantStates]);

  const toggleReferences = (messageIndex) => {
    setExpandedReferences((prev) => ({
      ...prev,
      [messageIndex]: !prev[messageIndex],
    }));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "end",
    });
  };

  const copyToClipboard = (e) => {
    e.stopPropagation();
    if (!chatId) return;

    navigator.clipboard
      .writeText(chatId)
      .then(() => {
        setCopied(true);
        setShowTooltip(true);
        setTimeout(() => {
          setCopied(false);
          setShowTooltip(false);
        }, 1500);
      })
      .catch((err) => {
        console.error("Failed to copy chat ID: ", err);
      });
  };

  useEffect(() => {
    if (props.language) {
      if (i18n.language !== props.language) {
        i18n.changeLanguage(props.language);
      }
    } else {
      const storedLang = localStorage.getItem("i18nextLng");
      if (storedLang && i18n.language !== storedLang) {
        i18n.changeLanguage(storedLang);
      }
    }
  }, [props.language, i18n]);

  ////////// POPUP ERROR /////////////
  const showStatusModal = useCallback(() => {
    setStatusModal(true);
    setEnded(true);
    setTimeout(() => {
      setStatusModal(false);
    }, 3500);
  }, []);

  useEffect(() => {
    const pathMatch = window.location.pathname.match(/\/projects\/([^/]+)/);
    const assistantIdFromPath = pathMatch ? pathMatch[1] : null;

    const currentRetrievalId = props.retrievalId || assistantIdFromPath;

    if (currentRetrievalId) {
      setSelectedRetrieval(currentRetrievalId);
    }

    if (
      currentRetrievalId &&
      (question == null || currentRetrievalId !== lastFetchedRetrievalId)
    ) {
      setLastFetchedRetrievalId(currentRetrievalId);

      const getInfoRetrieval = async (retrievalId) => {
        try {
          const data = await props.getRetrieval(retrievalId);
          setIniMessage(data.initial_message);
          setMaxMsgLength(data.max_msg_length);
          setIMaxResponses(data.max_responses);
          setStreamingAvailable(data.streaming_available);
          setMatrixModeAvailable(data.matrix_mode_available);
          setAudioAvailable(data.realtime_available);
          if (data.info) {
            setChatName(data.info);
          }
          if (data.logo) {
            setUrlLogo(data.logo);
          }
          if (data.colors) {
            setAssistantColors({
              nameColor: data.colors.nameColor || "",
              borderColor: data.colors.borderColor || "",
              primaryColor: data.colors.primaryColor || "",
              secondaryColor: data.colors.secondaryColor || "",
            });
          }
          setLoading(false);
        } catch (error) {
          showStatusModal();
          console.log(error);
          setLoading(false);
        }
      };
      getInfoRetrieval(currentRetrievalId);
      setAllowAsk(true);
    } else if (!currentRetrievalId) {
      setAllowAsk(false);
      setLoading(false);
    }

    scrollToBottom();

    const fetchRetrievals = async () => {
      try {
        const data = await getRetrievals();
        setRetrievals(data);
        setLoading(false);

        const effectiveRetrievalId = props.retrievalId || assistantIdFromPath;

        if (effectiveRetrievalId) {
          const found = data.find((r) => r.id === effectiveRetrievalId);
          if (found) {
            setCurrentRetrievalDescription(found.description);
            setSelectedRetrieval(effectiveRetrievalId);
            setStreamingAvailable(found.streaming_available);
            setMatrixModeAvailable(found.matrix_mode_available);
            setAudioAvailable(found.realtime_available);
          } else {
            console.log(
              "Retrieval not found in initial data, fetching directly"
            );
            try {
              const retrieval = await props.getRetrieval(effectiveRetrievalId);
              if (retrieval) {
                if (retrieval.description) {
                  setCurrentRetrievalDescription(retrieval.description);
                }
                setStreamingAvailable(retrieval.streaming_available);
                setMatrixModeAvailable(retrieval.matrix_mode_available);
                setAudioAvailable(retrieval.realtime_available);
              }
            } catch (error) {
              console.error("Error fetching retrieval directly:", error);
            }
          }
        }
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    fetchRetrievals();
  }, [
    messages,
    props,
    question,
    props.retrievalId,
    lastFetchedRetrievalId,
    showStatusModal,
  ]);

  useEffect(() => {
    if (assistantColors && assistantColors.borderColor) {
      document.documentElement.style.setProperty(
        "--assistant-border-color",
        assistantColors.borderColor
      );
    } else {
      document.documentElement.style.setProperty(
        "--assistant-border-color",
        "black"
      );
    }
  }, [assistantColors]);

  const requestStatus = (status) => {
    if (status > MAX_RESP_STATUS) {
      setEndedStatus(status);
    }
  };

  ////////// MATRIX MODE /////////////
  const openMatrixModal = () => {
    setMatrixModal(true);
  };

  const executeMatrixMode = async () => {
    setMatrixModal(false);
    setActivatingMatrix(true);
    try {
      const response = await props.addMatrixMode(
        question.id,
        props.matrix_mode
      );
      console.log(response);
      setActiveMatrix(true);
      setMessages(response.data.messages);
      setQuestion(response.data);
      setMaxMsgLength(response.data.max_msg_length);
    } catch (error) {
      showStatusModal();
      console.error("Error:", error);
    } finally {
      setActivatingMatrix(false);
    }
  };

  //////////// REFERENCES ////////////////
  const getUsedBlocks = (message) => {
    let usedBlocksSet = new Set();
    // The references can appear in the answer in the form of [1,2,3] or [1][2][3]
    let regex = /\[((\d+)(,( )*\d+)*)\]/g;
    let match = regex.exec(message.content);
    while (match != null) {
      let blocks = match[1].split(",");
      blocks.forEach((block) => usedBlocksSet.add(+block));
      match = regex.exec(message.content);
    }
    let usedBlocks = Array.from(usedBlocksSet);
    return usedBlocks;
  };

  const startNewChat = useCallback(async () => {
    if (!props.retrievalId) return;

    setLoading(true);
    resetAssistantStates();

    // Dispatch an event to reset feedback component
    const resetEvent = new Event("resetFeedbackState");
    document.dispatchEvent(resetEvent);

    try {
      const data = await props.getRetrieval(props.retrievalId);
      setIniMessage(data.initial_message);
      setMaxMsgLength(data.max_msg_length);
      setIMaxResponses(data.max_responses);
      setAllowAsk(true);
    } catch (error) {
      console.error("Error starting new chat:", error);
      showStatusModal();
    } finally {
      setLoading(false);
    }
  }, [resetAssistantStates, showStatusModal, props]);

  // Handle retrieval selection
  const handleRetrievalChange = (value) => {
    setLoading(true);
    setSelectedRetrieval(value);
    resetAssistantStates();
    setIniMessage(null);

    const selected = retrievals.find((r) => r.id === value);
    if (selected) {
      setCurrentRetrievalDescription(selected.description);
      setStreamingAvailable(selected.streaming_available);
      setMatrixModeAvailable(selected.matrix_mode_available);
      setAudioAvailable(selected.realtime_available);
    }
    navigate(`/projects/${value}/`);
  };

  /////////////// FUNCTION TO SEND MESSAGES //////////////
  const sendMessage = async () => {
    if (inputValue.trim() === "") return;
    setEndedStatus(0);
    setStreamEnded(false);

    // USER MESSAGE
    const userMessage = {
      content: inputValue,
      role: "user",
      usr_msg_num: cantResponses,
      attachment: messageFile,
    };

    if (messageFile) {
      setAttachments((prev) => [
        ...prev,
        { messageIndex: messages.length, file: messageFile },
      ]);
    }

    setcantResponses(cantResponses + 1);
    setMessages([...messages, userMessage]);
    setInputValue("");
    setIsLoading(true);

    // LOADING MESSAGE
    const loadingMessage = {
      content: t("thinking"),
      role: "assistant",
      loading: true,
    };

    setMessages((prevMessages) => [...prevMessages, loadingMessage]);

    try {
      // HANDLE STREAMING MODE
      if (streamingAvailable) {
        if (question === null) {
          fetch_stream(
          `cognitive/assistants/${props.retrievalId}/chats_stream/`,
          {
            'message': inputValue
          },
          (chunk) => {
            if (chunk.includes('[END OF MESSAGE]')) {
              // content after last newline
              const lastline_regex = /\n\[END OF MESSAGE\]\n/;
              // add chunk to message removing the last line
              setMessages((prevMessages) => [
                ...prevMessages.slice(0, -1),
                {
                  content: prevMessages[prevMessages.length - 1].loading ? chunk.replace(lastline_regex, '').replace(lastline_regex, '') : `${prevMessages[prevMessages.length - 1].content + chunk.replace(lastline_regex, '')}`,
                  is_user: false,
                  loading: false
                }
              ]);

              const contentAfterEnd = chunk.split(lastline_regex)[1] || "";
              if (!(contentAfterEnd === "")) {
                try {
                  const jsonData = JSON.parse(contentAfterEnd); // Convierte el string capturado a JSON
                  setQuestion(jsonData);

                  // set list of messages
                  if (iniMessage || iniMessage === "") {
                    setMessages(jsonData.messages.slice(1)); // Exclude initial message
                  } else {
                    setMessages(jsonData.messages);
                  }
                  setIsLoading(false);
                } catch (error) {
                  console.log(error);
                }
              }

              streamEnded = true;

            } else {
              if (streamEnded) {
                  const jsonData = JSON.parse(chunk); // Convierte el string capturado a JSON
                  setQuestion(jsonData);

                  // set list of messages
                  if (iniMessage || iniMessage === "") {
                    setMessages(jsonData.messages.slice(1)); // Exclude initial message
                  } else {
                    setMessages(jsonData.messages);
                  }
                  setIsLoading(false);
                }
              else{  // no es el último chunk
                  setMessages((prevMessages) => [
                    ...prevMessages.slice(0, -1),
                    {
                      content: prevMessages[prevMessages.length - 1].loading ? chunk : `${prevMessages[prevMessages.length - 1].content + chunk}`,
                      is_user: false,
                      loading: false
                    }
                  ]);
                }
            }
          }
        )

      } else {

          fetch_stream(
            `cognitive/chats_stream/${question.id}/`,
            {
              'message': inputValue
            },
            (chunk) => {
              if (chunk.includes('[END OF MESSAGE]')) {
                // content after last newline
                const lastline_regex = /\n\[END OF MESSAGE\]\n/;
                // add chunk to message removing the last line
                setMessages((prevMessages) => [
                  ...prevMessages.slice(0, -1),
                  {
                    content: prevMessages[prevMessages.length - 1].loading ? chunk.replace(lastline_regex, '').replace(lastline_regex, '') : `${prevMessages[prevMessages.length - 1].content + chunk.replace(lastline_regex, '')}`,
                    is_user: false,
                    loading: false
                  }
                ]);

                const contentAfterEnd = chunk.split(lastline_regex)[1] || "";
                if (!(contentAfterEnd === "")) {
                  try {
                    const jsonData = JSON.parse(contentAfterEnd); // Convierte el string capturado a JSON
                    setQuestion(jsonData);

                    // set list of messages
                    if (iniMessage || iniMessage === "") {
                      setMessages(jsonData.messages.slice(1)); // Exclude initial message
                    } else {
                      setMessages(jsonData.messages);
                    }
                    setIsLoading(false);
                  } catch (error) {
                    console.log(error);
                  }
                }

                streamEnded = true;

              } else {
                if (streamEnded) {
                    const jsonData = JSON.parse(chunk); // Convierte el string capturado a JSON
                    setQuestion(jsonData);

                    // set list of messages
                    if (iniMessage || iniMessage === "") {
                      setMessages(jsonData.messages.slice(1)); // Exclude initial message
                    } else {
                      setMessages(jsonData.messages);
                    }
                    setIsLoading(false);
                  }
                else{  // no es el último chunk
                    setMessages((prevMessages) => [
                      ...prevMessages.slice(0, -1),
                      {
                        content: prevMessages[prevMessages.length - 1].loading ? chunk : `${prevMessages[prevMessages.length - 1].content + chunk}`,
                        is_user: false,
                        loading: false
                      }
                    ]);
                  }
              }
            }
          );
        }
      } else {
        // HANDLE NON-STREAMING MODE (original code)
        let response = null;
        if (question != null) {
          response = await props.addMessage(
            inputValue,
            question.id,
            messageFile
          );
        } else {
          response = await props.addQuestionRetData(
            inputValue,
            props.retrievalId,
            messageFile
          );
        }

        setMessageFile(null);
        setLoading(false); // Turn off loading when response is received

        // see if errors
        if (response.status) {
          requestStatus(response.status);
          if (endedStatus > MAX_RESP_STATUS) {
            setEnded(true);
            showStatusModal();
          } else {
            setEnded(response.data.execution_status === "ENDED");
          }
        }

        // set Question chat
        setQuestion(response.data);
        setChatId(response.data.id);

        // Update messages with attachment
        const updatedMessages = response.data.messages.map((msg, index) => {
          if (index < messages.length && messages[index].attachment) {
            return {
              ...msg,
              attachment: messages[index].attachment,
            };
          }
          return msg;
        });

        // set list of messages
        if (iniMessage) {
          setMessages(updatedMessages.slice(1)); // Exclude initial message
        } else {
          setMessages(updatedMessages);
        }
      }
    } catch (error) {
      console.error("Error:", error);
      showStatusModal();
      setLoading(false); // Turn off loading on error
    } finally {
      setIsLoading(false);
      setMessageFile(null);
    }
  };

  //////////////// RETURN FUNCTION //////////////////////
  return (
    <div className="mt-10">
      <div
        className="w-full p-4 space-y-3 rounded-2xl flex flex-col custom-border mb-10"
        style={{ minHeight: "80vh", position: "relative" }}
      >
        {/* Messages area */}
        <div
          className="flex-grow overflow-y-auto mb-4 mt-3"
          style={{ minHeight: "500px" }}
        >
          {loading && (
            <div>
              <Lottie
                animationData={animationData}
                style={{
                  height: "260px",
                  width: "260px",
                  margin: "auto",
                  marginTop: "120px",
                }}
              />
            </div>
          )}
          {!loading && !props.retrievalId ? (
            <div
              className="flex items-center justify-center h-full"
              style={{ marginTop: "100px" }}
            >
              <div className="text-center p-6 bg-gray-50 rounded-lg shadow-sm">
                <h2 className="text-xl font-medium mb-4">
                  {t("selectAssistant")}
                </h2>
                <p className="text-gray-500 mb-6">{t("chooseAssistant")}</p>
                <div className="flex justify-center">
                  <Select
                    options={retrievals.map((retrieval) => ({
                      value: retrieval.id,
                      label: retrieval.description,
                    }))}
                    size="large"
                    placeholder={t("selectAssistantDropdown")}
                    style={{ width: "100%" }}
                    onChange={handleRetrievalChange}
                    listHeight={250}
                    dropdownStyle={{ borderColor: "black" }}
                    className="custom-black-select"
                    disabled={envRetrieval}
                  />
                </div>
              </div>
            </div>
          ) : (
            !loading && (
              <>
                {iniMessage != null && iniMessage !== "" && (
                  ////////////////// show initial message outside list of messages ///////////////////////
                  <Message
                    isUser={false}
                    text={
                      <div className="text-base font-sans md:text-md">
                        {linkify(iniMessage)}
                      </div>
                    }
                    img_logo={urlLogo}
                    colors={
                      assistantColors && assistantColors.nameColor
                        ? assistantColors.nameColor
                        : "black"
                    }
                    chat_name={chatName}
                  />
                )}

                {messages.map((message, index) =>
                  ////////////////// iterate in list of messages  ///////////////////////
                  message.role === "user" ? (
                    ////// User message //////
                    <Message
                      key={index}
                      isUser={true}
                      num_msgs={message.usr_msg_num}
                      max_msgs={maxResponses}
                      colors={
                        assistantColors && assistantColors.nameColor
                          ? assistantColors.nameColor
                          : "black"
                      }
                      text={
                        <>
                          <div
                            className="text-base md:text-md"
                            dangerouslySetInnerHTML={{
                              __html: linkify(
                                message.content.replace(/\n/g, "<br />")
                              ),
                            }}
                          />
                          {attachments.find(
                            (att) => att.messageIndex === index
                          ) && (
                            <div className="flex items-center mt-2 text-xs text-gray-500">
                              <AttachFileIcon fontSize="small" />
                              <a
                                href={URL.createObjectURL(
                                  attachments.find(
                                    (att) => att.messageIndex === index
                                  ).file
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-1 underline"
                              >
                                {
                                  attachments.find(
                                    (att) => att.messageIndex === index
                                  ).file.name
                                }
                              </a>
                            </div>
                          )}
                        </>
                      }
                    />
                  ) : ////// Assistant message //////
                  index > 0 ? (
                    ///// Assistant message, but not first one //////
                    <Message
                      key={index}
                      isUser={false}
                      img_logo={urlLogo}
                      chat_name={chatName}
                      colors={
                        assistantColors && assistantColors.nameColor
                          ? assistantColors.nameColor
                          : "black"
                      }
                      text={
                        message.loading ? (
                          ///// Assistant message, loading//////
                          <ThinkingAnimation
                            baseText={message.content.replace(/\.+$/, "")}
                          />
                        ) : (
                          ///// Assistant message, from messages //////
                          <div>
                            <div className="text-justify whitespace-pre-line">
                            <Markdown
                              className="items-end font-sans md:text-md"
                              components={{
                                a: ({ children, ...props }) => (
                                  <a target="_blank" rel="noreferrer" {...props}>
                                    {children}
                                  </a>
                                ),
                              }}
                            >
                              {message.content.replace(/\\+/g, "\\")}
                            </Markdown>
                            </div>
                            {!streamingAvailable &&
                              getUsedBlocks(message).length > 0 && (
                                <div className="mt-3">
                                  <div
                                    className="flex items-center cursor-pointer"
                                    onClick={() => toggleReferences(index)}
                                  >
                                    <h4 className="font-medium m-0">
                                      {t("references")}
                                    </h4>
                                    {expandedReferences[index] ? (
                                      <KeyboardArrowUpIcon
                                        fontSize="small"
                                        className="ml-1"
                                      />
                                    ) : (
                                      <KeyboardArrowDownIcon
                                        fontSize="small"
                                        className="ml-1"
                                      />
                                    )}
                                  </div>
                                  {expandedReferences[index] && (
                                    <div className="pl-2 mt-1">
                                      {messages[index].references.map(
                                        (block, index2) => {
                                          const refMatch = block.source.match(
                                            /^(.*?)\s*(https:\/\/\S+)/
                                          );
                                          const refName = refMatch
                                            ? refMatch[1].trim()
                                            : block.source;
                                          const refUrl = refMatch
                                            ? refMatch[2].trim()
                                            : "";
                                          return (
                                            <p key={index2}>
                                              {"[" +
                                                block.reference_num +
                                                "] "}
                                              {refUrl ? (
                                                <a
                                                  href={refUrl}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                >
                                                  {refName || refUrl}
                                                </a>
                                              ) : (
                                                refName
                                              )}
                                            </p>
                                          );
                                        }
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        )
                      }
                    />
                  ) : (
                    <></>
                  )
                )}
                {/* Show activating matrix message */}
                {activatingMatrix && (
                  <Message
                    isUser={false}
                    chat_name={chatName || ""}
                    img_logo={urlLogo}
                    colors={
                      assistantColors && assistantColors.nameColor
                        ? assistantColors.nameColor
                        : "black"
                    }
                    text={
                      <ThinkingAnimation
                        baseText={t("activatingSelfImprovement")}
                      />
                    }
                  />
                )}
              </>
            )
          )}
        </div>
        <div ref={messagesEndRef} className="h-2" />
        <div
          className={`bg-white rounded-lg p-3 border-2 border-black w-10/12 lg:w-6/12 mx-auto custom-border-prompt`}
        >
          {!props.retrievalId ||
          (maxResponses >= cantResponses && ended === false) ? (
            <div className="flex flex-col gap-1">
              {/* Row 1: Selector */}
              <Select
                options={retrievals.map((retrieval) => ({
                  value: retrieval.id,
                  label: retrieval.description,
                }))}
                size="small"
                placeholder={t("selectAssistantDropdown")}
                className="w-fit"
                variant="borderless"
                style={{ minWidth: "185px" }}
                placement="topLeft"
                onChange={handleRetrievalChange}
                labelInValue={false}
                listItemHeight={10}
                listHeight={250}
                disabled={envRetrieval || !props.retrievalId}
                value={selectedRetrieval}
                labelRender={() => {
                  if (currentRetrievalDescription) {
                    return (
                      <span
                        style={{
                          color:
                            assistantColors && assistantColors.nameColor
                              ? assistantColors.nameColor
                              : "inherit",
                        }}
                      >
                        {currentRetrievalDescription}
                      </span>
                    );
                  }

                  const currentId =
                    selectedRetrieval ||
                    props.retrievalId ||
                    (() => {
                      const pathMatch =
                        window.location.pathname.match(/\/projects\/([^/]+)/);
                      return pathMatch ? pathMatch[1] : null;
                    })();

                  if (currentId) {
                    const found = retrievals.find((r) => r.id === currentId);
                    if (found)
                      return (
                        <span
                          style={{
                            color:
                              assistantColors && assistantColors.nameColor
                                ? assistantColors.nameColor
                                : "inherit",
                          }}
                        >
                          {found.description}
                        </span>
                      );
                  }

                  return (
                    <span
                      style={{
                        color:
                          assistantColors && assistantColors.nameColor
                            ? assistantColors.nameColor
                            : "inherit",
                      }}
                    >
                      {t("selectAssistantDropdown")}
                    </span>
                  );
                }}
                optionLabelProp="label"
                optionFilterProp="label"
                fieldNames={{ label: "label", value: "value" }}
              />

              {/* Row 2: Input field */}
              <div className="w-full mb-2">
                <OutlinedInput
                  className="w-full bg-white min-h-11 rounded-full"
                  size="small"
                  multiline
                  error={inputValue.length > maxMsgLength}
                  type="text"
                  placeholder={t("typeQuestion")}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      {!audioOn && props.retrievalId && (
                        <p
                          className={
                            "text-xs mr-2 " +
                            (inputValue.length > maxMsgLength
                              ? "text-red-500"
                              : "text-slate-500")
                          }
                        >
                          {inputValue.length}/{maxMsgLength}
                        </p>
                      )}
                      <IconButton
                        size="small"
                        disabled={
                          isLoading ||
                          activatingMatrix ||
                          inputValue.length > maxMsgLength ||
                          maxResponses * 2 === messages.length ||
                          allowAsk === false
                        }
                        onClick={() => {
                          sendMessage(inputValue);
                          setInputValue("");
                        }}
                        sx={{
                          backgroundColor: "transparent",
                          border: "2px solid",
                          borderColor:
                            assistantColors && assistantColors.primaryColor
                              ? assistantColors.primaryColor
                              : "black",
                          borderRadius: "50%",
                          "&:hover": {
                            backgroundColor:
                              assistantColors && assistantColors.primaryColor
                                ? assistantColors.primaryColor
                                : "#000000",
                            borderColor:
                              assistantColors && assistantColors.primaryColor
                                ? assistantColors.primaryColor
                                : "#000000",
                            "& .MuiSvgIcon-root": {
                              color: "white",
                            },
                          },
                        }}
                      >
                        {audioOn ? (
                          <GraphicEqIcon
                            sx={{
                              color:
                                assistantColors && assistantColors.primaryColor
                                  ? assistantColors.primaryColor
                                  : "black",
                            }}
                          />
                        ) : (
                          <ArrowForwardIcon
                            sx={{
                              color:
                                assistantColors && assistantColors.primaryColor
                                  ? assistantColors.primaryColor
                                  : "black",
                            }}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.shiftKey) {
                      setInputValue(inputValue + "\n");
                    } else if (e.key === "Enter") {
                      if (messageFile) {
                        sendMessage(inputValue, messageFile);
                      } else {
                        sendMessage(inputValue);
                      }
                      setInputValue("");
                    }
                  }}
                  disabled={isLoading || activatingMatrix || allowAsk === false}
                />
              </div>

              {/* Row 3: Buttons */}
              <div className="flex justify-between items-center">
                {/* Left side buttons */}
                <div className="flex items-center gap-2">
                  {props.allow_files && (
                    <>
                      {messageFile ? (
                        <Chip
                          variant="outlined"
                          icon={
                            <AttachFileIcon
                              fontSize="small"
                              sx={{
                                color:
                                  assistantColors &&
                                  assistantColors.secondaryColor
                                    ? assistantColors.secondaryColor
                                    : "black",
                              }}
                            />
                          }
                          label={messageFile.name}
                          onDelete={() => setMessageFile(null)}
                          disabled={!props.retrievalId}
                          size="small"
                          sx={{
                            borderColor:
                              assistantColors && assistantColors.secondaryColor
                                ? assistantColors.secondaryColor
                                : "black",
                            borderRadius: "16px",
                            height: "28px",
                            paddingX: "4px",
                            opacity: !props.retrievalId ? 0.5 : 1,
                            "& .MuiChip-label": {
                              maxWidth: "120px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.75rem",
                              color:
                                assistantColors &&
                                assistantColors.secondaryColor
                                  ? assistantColors.secondaryColor
                                  : "black",
                            },
                            "& .MuiChip-deleteIcon": {
                              color:
                                assistantColors &&
                                assistantColors.secondaryColor
                                  ? assistantColors.secondaryColor
                                  : "black",
                              fontSize: "0.85rem",
                            },
                          }}
                        />
                      ) : (
                        <Chip
                          variant="outlined"
                          icon={
                            <AttachFileIcon
                              sx={{
                                color:
                                  assistantColors &&
                                  assistantColors.secondaryColor
                                    ? assistantColors.secondaryColor
                                    : "black",
                              }}
                            />
                          }
                          onClick={() =>
                            props.retrievalId &&
                            document.getElementById("file-input").click()
                          }
                          disabled={!props.retrievalId}
                          size="small"
                          sx={{
                            borderColor:
                              assistantColors && assistantColors.secondaryColor
                                ? assistantColors.secondaryColor
                                : "black",
                            borderRadius: "50%",
                            width: "28px",
                            height: "28px",
                            paddingX: "4px",
                            opacity: !props.retrievalId ? 0.5 : 1,
                            "& .MuiChip-icon": {
                              margin: 0,
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              transform: "translate(-50%, -50%)",
                              fontSize: "0.85rem",
                              color:
                                assistantColors &&
                                assistantColors.secondaryColor
                                  ? assistantColors.secondaryColor
                                  : "black",
                            },
                            "&:hover": {
                              backgroundColor: props.retrievalId
                                ? (assistantColors &&
                                  assistantColors.primaryColor
                                    ? assistantColors.primaryColor
                                    : "black") + " !important"
                                : "transparent",
                              "& .MuiChip-icon": {
                                color: props.retrievalId
                                  ? "white"
                                  : assistantColors &&
                                    assistantColors.secondaryColor
                                  ? assistantColors.secondaryColor
                                  : "black",
                              },
                            },
                          }}
                        />
                      )}
                      <input
                        type="file"
                        id="file-input"
                        accept=".pdf,.png,.jpg,.jpeg"
                        style={{ display: "none" }}
                        disabled={!props.retrievalId}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setMessageFile(file);
                          }
                        }}
                      />
                    </>
                  )}

                  {/* Audio button - conditionally render based on audioAvailable */}
                  {audioAvailable && (
                    <Chip
                      variant="outlined"
                      icon={<MicNoneOutlinedIcon fontSize="small" />}
                      label={t("audio")}
                      onClick={() => props.retrievalId && setAudioOn(!audioOn)}
                      disabled={!props.retrievalId}
                      size="small"
                      sx={{
                        borderColor:
                          assistantColors && assistantColors.secondaryColor
                            ? assistantColors.secondaryColor
                            : "black",
                        borderRadius: "16px",
                        height: "28px",
                        paddingX: "4px",
                        backgroundColor:
                          audioOn && props.retrievalId
                            ? assistantColors && assistantColors.primaryColor
                              ? assistantColors.primaryColor
                              : "black"
                            : "transparent",
                        color:
                          audioOn && props.retrievalId
                            ? "white"
                            : assistantColors && assistantColors.secondaryColor
                            ? assistantColors.secondaryColor
                            : "black",
                        opacity: !props.retrievalId ? 0.5 : 1,
                        "& .MuiChip-icon": {
                          color:
                            audioOn && props.retrievalId
                              ? "white"
                              : assistantColors &&
                                assistantColors.secondaryColor
                              ? assistantColors.secondaryColor
                              : "black",
                        },
                        "&:hover": {
                          backgroundColor: props.retrievalId
                            ? (assistantColors && assistantColors.primaryColor
                                ? assistantColors.primaryColor
                                : "black") + " !important"
                            : "transparent",
                          "& .MuiChip-icon, & .MuiChip-label": {
                            color: props.retrievalId ? "white" : "inherit",
                          },
                        },
                      }}
                    />
                  )}

                  {/* SIM button - conditionally render based on matrixModeAvailable */}
                  {matrixModeAvailable && (
                    <Chip
                      variant="outlined"
                      icon={<AutoAwesomeOutlinedIcon fontSize="small" />}
                      label={t("sim")}
                      onClick={
                        props.retrievalId &&
                        !activatingMatrix &&
                        openMatrixModal
                      }
                      size="small"
                      disabled={
                        activeMatrix ||
                        activatingMatrix ||
                        question === null ||
                        !props.retrievalId
                      }
                      sx={{
                        borderColor:
                          assistantColors && assistantColors.secondaryColor
                            ? assistantColors.secondaryColor
                            : "black",
                        borderRadius: "16px",
                        height: "28px",
                        paddingX: "4px",
                        backgroundColor: activeMatrix
                          ? assistantColors && assistantColors.primaryColor
                            ? assistantColors.primaryColor
                            : "black"
                          : "transparent",
                        color: activeMatrix
                          ? "white"
                          : assistantColors && assistantColors.secondaryColor
                          ? assistantColors.secondaryColor
                          : "black",
                        opacity:
                          !props.retrievalId || activatingMatrix ? 0.5 : 1,
                        "& .MuiChip-icon, & .MuiChip-label": {
                          color: activeMatrix
                            ? "white"
                            : assistantColors && assistantColors.secondaryColor
                            ? assistantColors.secondaryColor
                            : "black",
                        },
                        "&:hover": {
                          backgroundColor:
                            props.retrievalId &&
                            !activeMatrix &&
                            !activatingMatrix &&
                            question !== null
                              ? (assistantColors && assistantColors.primaryColor
                                  ? assistantColors.primaryColor
                                  : "black") + " !important"
                              : "transparent",
                          "& .MuiChip-icon, & .MuiChip-label": {
                            color:
                              props.retrievalId &&
                              !activeMatrix &&
                              !activatingMatrix &&
                              question !== null
                                ? "white"
                                : "inherit",
                          },
                        },
                        "&.Mui-disabled": {
                          opacity: activeMatrix ? 1 : 0.5,
                          backgroundColor: activeMatrix
                            ? assistantColors && assistantColors.primaryColor
                              ? assistantColors.primaryColor
                              : "black"
                            : "transparent",
                          color: activeMatrix ? "white" : "inherit",
                          "& .MuiChip-icon": {
                            color: activeMatrix
                              ? "white"
                              : assistantColors &&
                                assistantColors.secondaryColor
                              ? assistantColors.secondaryColor
                              : "black",
                          },
                        },
                      }}
                    />
                  )}
                </div>

                {/* Right side buttons */}
                <div className="flex items-center gap-2 mr-3">
                  {/* Feedback component */}
                  <Feedback
                    colors={assistantColors}
                    id={question ? question.id : null}
                    addFeedback={props.addFeedback}
                    addFeedback2={props.addFeedback2}
                    small={true}
                    disabled={!props.retrievalId}
                    retrievalId={props.retrievalId}
                    selectedRetrieval={selectedRetrieval}
                  />

                  {/* Change topic button */}
                  <Chip
                    variant="outlined"
                    icon={<AddIcon fontSize="small" />}
                    label={t("newChat")}
                    onClick={props.retrievalId ? startNewChat : undefined}
                    disabled={!props.retrievalId}
                    size="small"
                    sx={{
                      borderColor:
                        assistantColors && assistantColors.secondaryColor
                          ? assistantColors.secondaryColor
                          : "black",
                      borderRadius: "16px",
                      height: "28px",
                      paddingX: "4px",
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color:
                        assistantColors && assistantColors.secondaryColor
                          ? assistantColors.secondaryColor
                          : "black",
                      opacity: !props.retrievalId ? 0.5 : 1,
                      "& .MuiChip-icon": {
                        color:
                          assistantColors && assistantColors.secondaryColor
                            ? assistantColors.secondaryColor
                            : "black",
                      },
                      "&:hover": {
                        backgroundColor: props.retrievalId
                          ? (assistantColors && assistantColors.primaryColor
                              ? assistantColors.primaryColor
                              : "black") + " !important"
                          : "transparent",
                        "& .MuiChip-icon, & .MuiChip-label": {
                          color: props.retrievalId ? "white" : "inherit",
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center px-3 text-sm border-0 text-slate-500">
              {ended &&
              endedStatus <= MAX_RESP_STATUS &&
              activeMatrix === false ? (
                <p className="mr-1">{t("endedConversation")}</p>
              ) : ended && activeMatrix === true ? (
                <p className="mr-1">{t("selfImprovementFinished")}</p>
              ) : endedStatus > MAX_RESP_STATUS ? (
                <p className="mr-1">{t("systemError")}</p>
              ) : (
                <p className="mr-1">
                  {t("maxQuestionsReached", { maxResponses })}
                </p>
              )}
              <Link component="button" variant="inherit" onClick={startNewChat}>
                {t("startNewChat")}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Matrix modal */}
      {matrixModal && (
        <div className="fixed inset-0 z-100 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg w-11/12 sm:w-96 max-w-md overflow-hidden">
            <div className="p-4 sm:p-5">
              <h2
                className="text-xl font-semibold mb-2"
                style={{
                  color:
                    assistantColors && assistantColors.nameColor
                      ? assistantColors.nameColor
                      : "black",
                }}
              >
                {t("selfImprovementMode")}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {t("activateSelfImprovement")}
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    borderColor:
                      assistantColors && assistantColors.borderColor
                        ? assistantColors.borderColor
                        : "black",
                    color:
                      assistantColors && assistantColors.borderColor
                        ? assistantColors.borderColor
                        : "black",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor:
                        assistantColors && assistantColors.borderColor
                          ? assistantColors.borderColor
                          : "black",
                    },
                  }}
                  onClick={() => setMatrixModal(false)}
                >
                  {t("cancel")}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    backgroundColor:
                      assistantColors && assistantColors.primaryColor
                        ? assistantColors.primaryColor
                        : "black",
                    "&:hover": {
                      backgroundColor:
                        assistantColors && assistantColors.primaryColor
                          ? assistantColors.primaryColor
                          : "black",
                      opacity: 0.9,
                    },
                  }}
                  onClick={executeMatrixMode}
                >
                  {t("start")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status modal */}
      {statusModal && (
        <div className="fixed inset-0 z-100">
          <div className="items-end justify-center px-4 pt-4 pb-20 m-40 text-center sm:block sm:p-0">
            <div className="inline-block px-8 pt-6 mt-20 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-xl shadow sm:align-middle">
              <div className="w-full mb-6">
                <p className="mt-2 mb-2 text-lg text-gray-500">
                  {t("systemError")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {chatId && (
        <div className="fixed bottom-2 right-4">
          <Tooltip
            title={
              <span style={{ fontSize: "1.5em", whiteSpace: "nowrap" }}>
                {copied ? t("chatIdCopied") : `Chat Id: ${chatId}`}
              </span>
            }
            placement="top-end"
            arrow
            sx={{ fontSize: 20 }}
            open={showTooltip}
            onClose={() => !copied && setShowTooltip(false)}
            PopperProps={{
              disablePortal: true,
              modifiers: [
                {
                  name: "preventOverflow",
                  enabled: true,
                  options: {
                    altAxis: true,
                    boundary: "viewport",
                  },
                },
                {
                  name: "flip",
                  enabled: false,
                },
              ],
            }}
          >
            <div
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => !copied && setShowTooltip(false)}
              onClick={copyToClipboard}
            >
              {copied ? (
                <CheckCircleIcon
                  fontSize="medium"
                  color="success"
                  sx={{ width: 60, cursor: "pointer" }}
                />
              ) : (
                <HelpOutlineIcon
                  fontSize="medium"
                  color="default"
                  sx={{ width: 60, cursor: "pointer" }}
                />
              )}
            </div>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default ShowQuestion;
